<script setup>
import { computed, ref, watch } from 'vue'
import { usePage, router } from '@inertiajs/vue3'
import OverviewChangeRequest from '../components/ui-components/overview/OverviewChangeRequest.vue'

const page = usePage()
const list = ref([])

watch(
    () => page.props.__neo_main?.__data?.pendingChangeRequests,
    (data) => { list.value = data ?? [] },
    { immediate: true }
)

const countByType = computed(() => ({
    cancel: list.value.filter(r => r.action === 'cancel' || r.cancel_requested).length,
    change: list.value.filter(r => r.action !== 'cancel' && !r.cancel_requested).length,
}))

function approve(id) {
    list.value = list.value.filter(r => r.id !== id)
    router.visit(route('app.forms.accept', { request: id }), { preserveState: true, preserveScroll: true })
}

function reject(id) {
    list.value = list.value.filter(r => r.id !== id)
    router.visit(route('app.forms.reject', { request: id }), { preserveState: true, preserveScroll: true })
}
</script>

<template>
    <!-- Summary pills -->
    <div v-if="list.length" class="flex items-center gap-3 mb-6">
        <div class="flex items-center gap-2 bg-white border border-gray-100 rounded-[12px] px-4 py-2.5 shadow-sm">
            <span class="text-[22px] font-bold text-gray-900 leading-none">{{ list.length }}</span>
            <span class="text-[13px] text-gray-400 font-medium">{{ $t('reservations.pending_requests') }}</span>
        </div>
        <div v-if="countByType.change" class="flex items-center gap-1.5 bg-blue-50 border border-blue-100 rounded-[12px] px-3.5 py-2.5">
            <span class="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"/>
            <span class="text-[13px] text-blue-700 font-semibold">{{ countByType.change }}× {{ $t('reservations.type_change') }}</span>
        </div>
        <div v-if="countByType.cancel" class="flex items-center gap-1.5 bg-red-50 border border-red-100 rounded-[12px] px-3.5 py-2.5">
            <span class="w-2 h-2 rounded-full bg-red-400 flex-shrink-0"/>
            <span class="text-[13px] text-red-600 font-semibold">{{ countByType.cancel }}× {{ $t('reservations.type_cancel') }}</span>
        </div>
    </div>

    <!-- Grid -->
    <div v-if="list.length" class="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 items-start">
            <OverviewChangeRequest
                v-for="req in list"
                :key="req.id"
                :request="req"
                class="!mb-0"
                @approve="approve"
                @reject="reject"
            />
        </div>
    </div>

    <!-- Empty state -->
    <div v-else class="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-24 text-center">
        <div class="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
            <i class="pi pi-check-circle text-2xl text-gray-400"/>
        </div>
        <p class="text-[16px] font-semibold text-gray-700">{{ $t('reservations.all_ok') }}</p>
        <p class="text-[13px] text-gray-400 mt-1.5 max-w-xs">{{ $t('reservations.no_pending_changes') }}</p>
    </div>
</template>
